import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPut } from "../../services/api";
import type { Transaction, Location } from "../../types";
import ReceiptModal from "./ReceiptModal";

export default function TransactionsTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState<Transaction | null>(null);
  const [filterLocation, setFilterLocation] = useState("");

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [txns, locs] = await Promise.all([
        apiGet<Transaction[]>(`/organizations/${orgId}/transactions`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
      ]);
      // Sort newest first
      setTransactions((txns as any[]).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const voidTransaction = async (txn: any) => {
    if (txn.paymentMethod !== "cash") {
      alert("Only cash transactions can be voided. For card/other payments, use Returns/Refunds instead.");
      return;
    }
    if (txn.status !== "completed") {
      alert("This transaction has already been " + txn.status + ".");
      return;
    }
    if (!confirm("⚠️ VOID TRANSACTION\n\nThis will void receipt #" + (txn.receiptNumber || txn.transactionId.slice(0,8)) + " ($" + (txn.total || 0).toFixed(2) + " cash).\n\nVoiding should only be used for same-session errors. For customer returns, use Returns/Refunds instead.\n\nThis cannot be undone. Continue?")) return;
    try {
      await apiPut(`/organizations/${orgId}/transactions/${txn.transactionId}`, { ...txn, status: "voided", voidedAt: new Date().toISOString(), voidedBy: user?.id });
      await loadData();
    } catch (err) { console.error(err); }
  };

  const getLocName = (id: string) => (locations.find((l: any) => l.locationId === id) as any)?.name || id;

  const filtered = filterLocation
    ? transactions.filter((t) => t.locationId === filterLocation)
    : transactions;

  if (loading) return <div className="empty-state">Loading transactions...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Transaction History</h2>
        <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} style={{ padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.85rem" }}>
          <option value="">All Locations</option>
          {locations.map((l: any) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><p>No transactions yet. Sales from the POS will appear here.</p></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Transaction ID</th>
              <th>Location</th>
              <th>Items</th>
              <th>Total</th>
              <th>Payment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((txn: any) => (
              <tr key={txn.transactionId}>
                <td style={{ fontSize: "0.85rem" }}>
                  {new Date(txn.createdAt).toLocaleDateString()}<br />
                  <span style={{ color: "#6b7280" }}>{new Date(txn.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                </td>
                <td><code style={{ fontSize: "0.9rem", background: "#f3f4f6", padding: "0.2rem 0.5rem", borderRadius: "4px", fontWeight: 600 }}>{txn.receiptNumber || txn.transactionId?.slice(0, 8)}</code></td>
                <td>{getLocName(txn.locationId)}</td>
                <td>{txn.items?.length || 0} items</td>
                <td style={{ fontWeight: 600 }}>${(txn.total || 0).toFixed(2)}</td>
                <td>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                    {txn.paymentMethod === "cash" ? "💵" : "💳"} {txn.paymentMethod}
                  </span>
                </td>
                <td>
                  <div className="table-actions">
                    <button onClick={() => setSelectedTxn(txn)}>Receipt</button><button className="delete" onClick={() => voidTransaction(txn)}>Void</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {selectedTxn && (
        <ReceiptModal
          transaction={selectedTxn}
          locationName={getLocName(selectedTxn.locationId)}
          onClose={() => setSelectedTxn(null)}
        />
      )}
    </div>
  );
}
